import React, { useState } from 'react';
import { Box, Collapse, Paper, Typography } from '@mui/material';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../../context/ThemeContext';

interface CollapsibleChartSectionProps {
  title: string;
  subtitle?: string;
  defaultExpanded?: boolean;
  /**
   * Render function — invoked only after the section has been expanded at least once.
   * Heavy chart components should sit inside this callback so they are not mounted
   * (and therefore not fetched / not laid out) until the user opens the section.
   */
  children: () => React.ReactNode;
  actions?: React.ReactNode;
}

const CollapsibleChartSection: React.FC<CollapsibleChartSectionProps> = ({
  title,
  subtitle,
  defaultExpanded = false,
  children,
  actions,
}) => {
  const { theme } = useTheme();
  const [expanded, setExpanded] = useState<boolean>(defaultExpanded);
  const [hasBeenExpanded, setHasBeenExpanded] = useState<boolean>(defaultExpanded);

  const toggle = () => {
    setExpanded((prev) => {
      const next = !prev;
      if (next) setHasBeenExpanded(true);
      return next;
    });
  };

  return (
    <Paper
      sx={{
        borderRadius: '12px',
        backgroundColor: theme.components?.card?.background || '#FFFFFF',
        border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
        mb: { xs: 2, sm: 3 },
        // Was 'hidden' — clipped chart tooltips that extend past the card (e.g. tall stacked-channel tooltip).
        overflow: 'visible',
      }}
    >
      <Box
        onClick={toggle}
        role="button"
        aria-expanded={expanded}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          px: { xs: 1.5, sm: 2.5 },
          py: { xs: 1.25, sm: 1.5 },
          cursor: 'pointer',
          userSelect: 'none',
          borderRadius: '12px 12px 0 0',
          transition: 'background-color 0.15s ease',
          '&:hover': { backgroundColor: `${theme.primary}06` },
          '&:focus-visible': { outline: `2px solid ${theme.primary}`, outlineOffset: -2 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
          <ChevronDownIcon
            style={{
              width: 18,
              height: 18,
              color: theme.gray?.[500] || '#666',
              transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform 0.2s ease',
              flexShrink: 0,
            }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 700,
                color: theme.text,
                fontSize: { xs: '0.95rem', sm: '1rem' },
                letterSpacing: '-0.01em',
                lineHeight: 1.2,
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                sx={{
                  color: theme.gray?.[500] || '#666',
                  fontSize: '0.75rem',
                  mt: 0.25,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
        {actions && (
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}
          >
            {actions}
          </Box>
        )}
      </Box>
      <Collapse in={expanded} timeout={220} mountOnEnter unmountOnExit={false}>
        <Box sx={{ px: { xs: 1.5, sm: 2.5 }, pb: { xs: 1.5, sm: 2.5 } }}>
          {hasBeenExpanded ? children() : null}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default CollapsibleChartSection;
